import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractConfigApi } from '../services/api';
import { useRefreshOnReturn } from '../hooks/useRefreshOnReturn';
import type { FundingNotificationStatus as Status } from '../types';

/** `compact`: one line with the latest request to the customer, for the folded-out "Betalning inväntas" box. */
export function FundingNotificationStatus({ projectId, refreshToken, compact = false }: { projectId: string; refreshToken: number; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const [notices, setNotices] = useState<Status[]>([]);
  const load = useCallback(async () => { setNotices((await contractConfigApi.getFundingNotifications(projectId)) ?? []); }, [projectId]);
  useEffect(() => { void load().catch(() => {}); }, [load, refreshToken]);
  useRefreshOnReturn(load);
  if (!notices.length) return null;
  const date = (value: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  if (compact) {
    const latest = notices.reduce((last, notice) => notice.notifiedAt > last.notifiedAt ? notice : last);
    return <p className="project-funding-awaiting-note mb-0" data-testid="funding-notification-status">
      <i className="feather-mail me-2" aria-hidden="true" />
      {t('fundingNotice.compactStatus', { date: date(latest.notifiedAt) })}
    </p>;
  }
  return <div className="border rounded p-3 mt-3 text-break" data-testid="funding-notification-status">
    <h3 className="h6">{t('fundingNotice.statusTitle')}</h3>
    {notices.map((notice, index) => <div key={index} className="small mb-2">
      <strong>{t(notice.reminder ? 'fundingNotice.reminderStatus' : 'fundingNotice.initialStatus', { contractNumber: notice.contractNumber })}</strong>
      {notice.inApp && <div>{t('fundingNotice.inAppSent', { date: date(notice.notifiedAt) })}</div>}
      <div>{notice.emailSentAt ? t('fundingNotice.emailSent', { date: date(notice.emailSentAt) }) : t('fundingNotice.emailNotSent')}</div>
    </div>)}
  </div>;
}
